import { HULLS_CONFIG } from './hulls.ts';
import { MODULES_CONFIG } from './modules.ts';
import type { ComputedShipStats, HullClass, WeaponDamageType } from '../types/ship.ts';

export function computeShipStats(
  blueprintId: string,
  name: string,
  hullId: HullClass,
  moduleIds: string[]
): ComputedShipStats {
  const hull = HULLS_CONFIG[hullId];
  if (!hull) {
    throw new Error(`Unknown hull class: ${hullId}`);
  }

  let usedCapacity = 0;
  let usedEnergy = 0;
  let weaponCount = 0;
  let defenseCount = 0;
  let engineCount = 0;

  let totalHp = hull.base_hp;
  let totalShield = hull.base_shield;
  let totalArmor = hull.base_armor;
  let totalDamage = 0;
  let damageType: WeaponDamageType = 'kinetic';
  let rangePhase: 1 | 2 | 3 = 2; // Medium by default
  let weaponAccuracies: number[] = [];

  let evasionBonus = 0;
  let speedBonus = 0;
  let initiativeBonus = 0;

  let costMetal = hull.cost.metal;
  let costHe3 = hull.cost.he3;
  let costCredits = hull.cost.credits;
  let buildTimeSec = hull.build_time_sec;

  for (const mid of moduleIds) {
    const mod = MODULES_CONFIG[mid];
    if (!mod) continue;

    usedCapacity += mod.weight;
    usedEnergy += mod.power_draw;

    costMetal += mod.cost.metal;
    costHe3 += mod.cost.he3;
    costCredits += mod.cost.credits;
    buildTimeSec += Math.floor(mod.weight * 0.4);

    if (mod.category === 'weapon') {
      weaponCount++;
      totalDamage += mod.damage ?? 0;
      if (mod.damage_type) damageType = mod.damage_type;
      if (mod.range_phase && mod.range_phase < rangePhase) {
        rangePhase = mod.range_phase; // Long range takes priority
      }
      if (mod.accuracy) weaponAccuracies.push(mod.accuracy);
    } else if (mod.category === 'shield') {
      defenseCount++;
      totalShield += mod.shield_hp ?? 0;
    } else if (mod.category === 'armor') {
      defenseCount++;
      totalArmor += mod.armor_value ?? 0;
      totalHp += mod.hp_bonus ?? 0;
    } else if (mod.category === 'engine') {
      engineCount++;
      speedBonus += mod.speed_bonus ?? 0;
      evasionBonus += mod.evasion_bonus ?? 0;
      initiativeBonus += mod.initiative_bonus ?? 0;
    }
  }

  const finalSpeed = hull.base_speed + speedBonus;
  const finalEvasion = Math.min(65, hull.base_evasion + evasionBonus);
  const finalInitiative = finalSpeed + initiativeBonus;
  const finalAccuracy = weaponAccuracies.length > 0
    ? Math.round(weaponAccuracies.reduce((a, b) => a + b, 0) / weaponAccuracies.length)
    : 80;

  let isValid = true;
  let validationError: string | undefined;

  if (usedCapacity > hull.base_capacity) {
    isValid = false;
    validationError = `Vượt quá tải trọng tối đa (${usedCapacity}/${hull.base_capacity})`;
  } else if (usedEnergy > hull.base_energy) {
    isValid = false;
    validationError = `Vượt quá công suất năng lượng (${usedEnergy}/${hull.base_energy})`;
  } else if (weaponCount > hull.max_weapon_slots) {
    isValid = false;
    validationError = `Vượt quá số lượng module vũ khí (${weaponCount}/${hull.max_weapon_slots})`;
  } else if (defenseCount > hull.max_defense_slots) {
    isValid = false;
    validationError = `Vượt quá số lượng module phòng thủ (${defenseCount}/${hull.max_defense_slots})`;
  } else if (engineCount > hull.max_engine_slots) {
    isValid = false;
    validationError = `Vượt quá số lượng module động cơ (${engineCount}/${hull.max_engine_slots})`;
  }

  return {
    blueprint_id: blueprintId,
    name,
    hull_id: hullId,
    total_hp: totalHp,
    total_shield: totalShield,
    total_armor: totalArmor,
    total_damage: Math.max(10, totalDamage),
    damage_type: damageType,
    range_phase: rangePhase,
    accuracy: finalAccuracy,
    evasion: finalEvasion,
    speed: finalSpeed,
    initiative: finalInitiative,
    used_capacity: usedCapacity,
    max_capacity: hull.base_capacity,
    used_energy: usedEnergy,
    max_energy: hull.base_energy,
    unit_cost: {
      metal: costMetal,
      he3: costHe3,
      credits: costCredits,
    },
    unit_build_time_sec: Math.max(5, buildTimeSec),
    is_valid: isValid,
    validation_error: validationError,
  };
}
